import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  useTheme,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

interface PaymentFailurePopupProps {
  open: boolean;
  onClose: () => void;
  message?:
    | string
    | {
        error_code?: string | number;
        transaction_id?: string;
        auth_response_text?: string;
        message?: string;
        stripe_error?: string;
        [key: string]: any;
      };
}

// Best-effort URL-decoder for values that came out of a form-encoded errorDescription
// like "Your+card+was+declined.". Never throws — falls back to the original string.
const decodeMaybe = (value?: string): string | undefined => {
  if (!value) return value;
  try {
    return decodeURIComponent(value.replace(/\+/g, " "));
  } catch {
    return value;
  }
};

const PaymentFailurePopup: React.FC<PaymentFailurePopupProps> = ({ open, onClose, message }) => {
  const theme = useTheme();

  const isObject = message !== null && typeof message === "object";
  const obj = isObject ? (message as { [key: string]: any }) : undefined;
  // Pick the most human-readable description we can find. Order is intentional:
  // auth_response_text wins (legacy field), then stripe's "message", then the
  // stripe error code, finally any raw string we were handed.
  const description =
    (obj && decodeMaybe(obj.auth_response_text)) ||
    (obj && decodeMaybe(obj.message)) ||
    (obj && decodeMaybe(obj.stripe_error)) ||
    (typeof message === "string" ? message : undefined);
  const hasAnyDetail =
    !!description ||
    !!(obj && (obj.error_code || obj.transaction_id));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <ErrorOutlineIcon color="error" />
          <Typography variant="h6" color="error" fontWeight="bold">
            Payment Unsuccessful
          </Typography>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          We couldn’t process your order. Please try again or contact support.
        </Typography>

        {hasAnyDetail && (
          <Box
            sx={{
              p: 2,
              border: `1px solid ${theme.palette.error.main}`,
              borderRadius: 1,
              backgroundColor: theme.palette.error.light,
              color: theme.palette.error.contrastText,
              fontSize: '0.875rem',
              fontFamily: 'monospace',
            }}
          >
            {description && (
              <Typography sx={{ mb: 1 }}>
                <strong>Error:</strong> {description}
              </Typography>
            )}
            {obj?.error_code && (
              <Typography sx={{ mb: 1 }}>
                <strong>Status Code:</strong> {obj.error_code}
              </Typography>
            )}
            {obj?.transaction_id && (
              <Typography>
                <strong>Transaction ID:</strong> {obj.transaction_id}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onClose}
          variant="contained"
          color="error"
          sx={{
            mr: 2,
            mb: 1,
            px: 4,
            fontWeight: 'bold',
            textTransform: 'none',
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentFailurePopup;
